import React, { useEffect, useState } from 'react';
import { Typography, Container, Box, Grid, TextField, useMediaQuery, ButtonBase } from '@material-ui/core';
import { SearchOutlined, DateRange, Note, Assignment } from '@material-ui/icons';
import { useHistory } from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { NotFoundFilterResponse } from '../common/components';
import { useStyles } from './indexStyle';
import { useLoader } from '../../../contexts/pharmaceutical-portal/Loader.context';
import { contentsIntegrationService } from '../../../services';
import { IContentIntegration } from '../common/contracts';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const RegulationPage: React.FC<any> = (): JSX.Element => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { showLoader, hideLoader } = useLoader();
  const [allEvents, setAllEvents] = useState<IContentIntegration[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<IContentIntegration[]>([]);
  const [allRegulations, setAllRegulations] = useState<IContentIntegration[]>([]);
  const [filteredRegulations, setFilteredRegulations] = useState<IContentIntegration[]>([]);
  const [searchEventsTerm, setSearchEventsTerm] = useState("");
  const [searchRegulationsTerm, setSearchRegulationsTerm] = useState("");

  const { data: event } = contentsIntegrationService.getContents(true, "event");
  const { data: regulation } = contentsIntegrationService.getContents(true, "regulation")

  useEffect(() => {
    showLoader();
    if (event?.data?.data) {
      setFilteredEvents(event.data.data);
      setAllEvents(event.data.data);
    }

    if (regulation?.data?.data) {
      setFilteredRegulations(regulation.data.data);
      setAllRegulations(regulation.data.data)
    }
    hideLoader();
  }, [event, regulation]);

  const filterEvents = (term: string) => {
    if (!term) {
      setFilteredEvents(allEvents);
      return;
    }

    const filtered = allEvents.filter(ev => {
      const matchesSearch =
        ev.title.toLowerCase().includes(term) ||
        ev.description.toLowerCase().includes(term);

      return matchesSearch;
    });

    setFilteredEvents(filtered);
  };

  const filterRegulations = (term: string) => {
    if (!term) {
      setFilteredRegulations(allRegulations);
      return;
    }

    const filtered = allRegulations.filter(regulation => {
      const matchesSearch =
        regulation.title.toLowerCase().includes(term);

      return matchesSearch;
    });

    setFilteredRegulations(filtered);
  };

  const handleSearchEventsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value.toLowerCase();
    setSearchEventsTerm(term);
    filterEvents(term);
  };

  const handleSearchRegulationsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value.toLowerCase();
    setSearchRegulationsTerm(term);
    filterRegulations(term);
  };

  const handleClick = (data: any) => {
    try {
      showLoader();
      const title = data?.title;
      const htmlContent = data?.body;
      const formattedUrl = `${data.id}-${title.replace(/\s+/g, "-")}`;
      const historyPath = '/panel/regulation';

      setTimeout(() => {
        history.push({
          pathname: `/panel/regulation/${formattedUrl}`,
          state: { htmlContent, title, historyPath },
        });
        hideLoader();
      }, 300);
    } catch (error) {
      console.error("Erro ao buscar evento:", error);
    }
  };

  return (
    <Container maxWidth="lg">
      {/* Seção de Eventos */}
      <Box mt={4}>
        <Box className={classes.sectionHeader}>
          <DateRange style={{ fontSize: '1.8rem' }} />
          <Typography variant="h1" className={classes.sectionTitle}>
            Eventos
          </Typography>
        </Box>
        <hr className={classes.sectionDivider} />

        <Grid container spacing={2} className={classes.searchAndDescriptionRow}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="h6" className={classes.sectionDescription}>
              Confira a programação de eventos.
            </Typography>
          </Grid>
          <Grid item xs={8} sm={4} md={4}>
            <TextField
              placeholder="Busque e explore eventos"
              variant="outlined"
              value={searchEventsTerm}
              onChange={handleSearchEventsChange}
              className={classes.textField}
              InputProps={{
                startAdornment: (
                  <Box>
                    <SearchOutlined />
                  </Box>
                ),
                style: { fontSize: isSmallScreen ? '0.9rem' : '1rem' },
              }}
            />
          </Grid>
        </Grid>

        {filteredEvents.length === 0 ? (
          <NotFoundFilterResponse />
        ) : (
          <Grid container spacing={3}>
            {filteredEvents.map((event) => (
              <Grid item xs={12} sm={6} md={4} key={event.id}>
                <ButtonBase 
                  className={classes.cardButtonBase} 
                  onClick={() => handleClick(event)} // Mudar quando receber o HTML de cada evento
                >
                  <Box className={classes.eventImagePlaceholder}>
                    <img src={event.image.thumbnailUrl} alt={event.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>

                  <Box className={classes.eventInfoRow}>
                    <Box className={classes.eventDateColumn}>
                      <Typography className={clsx(classes.eventMonthText, classes.orangeColor)}>
                        {parseISO(event.date).toLocaleString("pt-BR", { month: "short" })}
                      </Typography>
                      <Typography className={clsx(classes.eventDayText, classes.orangeColor)}>
                        {parseISO(event.date).getDate()}
                      </Typography>
                    </Box>
                    <Box className={classes.eventTitleLocationColumn}>
                      <Typography variant="subtitle2" className={classes.cardTitle}>
                        {event.title}
                      </Typography>
                      <Typography variant="body2" className={classes.cardSubtitle}>
                        {event.description}
                      </Typography>
                    </Box>
                  </Box>
                </ButtonBase>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Seção de Regulamentação */}
      <Box mt={6} mb={4}>
        <Box className={classes.sectionHeader}>
          <Note style={{ fontSize: '1.8rem' }} />
          <Typography variant="h1" className={classes.sectionTitle}>
            Regulamentação
          </Typography>
        </Box>
        <hr className={classes.sectionDivider} />

        <Grid container spacing={2} className={classes.searchAndDescriptionRow}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="h6" className={classes.sectionDescription}>
              Aqui você encontra documentos relevantes.
            </Typography>
          </Grid>
          <Grid item xs={8} sm={4} md={4}>
            <TextField
              placeholder="Busque por documentos"
              variant="outlined"
              value={searchRegulationsTerm}
              onChange={handleSearchRegulationsChange}
              className={classes.textField}
              InputProps={{
                startAdornment: (
                  <Box>
                    <SearchOutlined />
                  </Box>
                ),
                style: { fontSize: isSmallScreen ? '0.9rem' : '1rem' },
              }}
            />
          </Grid>
        </Grid>

        {filteredRegulations.length === 0 ? (
          <NotFoundFilterResponse />
        ) : (
          <Grid container spacing={3}>
            {filteredRegulations.map((regulation) => (
              <Grid item xs={12} sm={6} md={4} key={regulation.id}>
                <ButtonBase 
                  className={classes.cardButtonBase} 
                  onClick={() => handleClick(regulation)} // Mudar quando receber o HTML de cada regulamento
                >
                  <Box className={classes.eventInfoRow}>
                      <Box className={classes.regulationIconContainer}>
                        <Assignment style={{ fontSize: '2rem' }} />
                      </Box>
                      <Box className={classes.eventTitleLocationColumn}>
                        <Typography variant="subtitle2" className={classes.cardTitle}>
                          {regulation.title}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" className={classes.cardSubtitle}>
                          {format(parseISO(regulation.date), "MMMM/yyyy", { locale: ptBR }).replace(/^\w/, c => c.toUpperCase())}
                        </Typography>
                      </Box>
                    </Box>
                </ButtonBase>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default RegulationPage;